import type { GraphNode, GraphEdge } from '../../api/dastarkhan';

// Keys that are Neo4j metadata, not user properties
const NODE_META_KEYS = new Set(['element_id', 'labels']);
const EDGE_META_KEYS = new Set([
  'element_id', 'type',
  'source_element_id', 'target_element_id',
  'start_node_element_id', 'end_node_element_id',
]);

/**
 * Check if an object looks like a Neo4j node.
 * Handles both API format ({element_id, labels, properties: {}})
 * and raw record.data() format ({element_id, labels, ...props_spread}).
 */
function isNodeLike(obj: Record<string, unknown>): boolean {
  return !!(obj.element_id && obj.labels && Array.isArray(obj.labels));
}

/**
 * Check if an object looks like a Neo4j edge.
 * Handles both API format (source_element_id/target_element_id)
 * and raw record.data() format (start_node_element_id/end_node_element_id).
 */
function isEdgeLike(obj: Record<string, unknown>): boolean {
  if (!obj.element_id || !obj.type) return false;
  return !!(
    (obj.source_element_id && obj.target_element_id) ||
    (obj.start_node_element_id && obj.end_node_element_id)
  );
}

/**
 * Extract user properties from a node/edge object.
 * If `properties` sub-object exists (API format), use it.
 * Otherwise collect all non-metadata keys (record.data() format).
 */
function extractProperties(obj: Record<string, unknown>, metaKeys: Set<string>): Record<string, unknown> {
  if (obj.properties && typeof obj.properties === 'object' && !Array.isArray(obj.properties)) {
    return obj.properties as Record<string, unknown>;
  }
  const props: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (!metaKeys.has(k) && k !== 'properties') {
      props[k] = v;
    }
  }
  return props;
}

/**
 * Derive a human-readable column header from Cypher variable names.
 * Inspects cell values to determine the entity type.
 */
export function deriveColumnHeader(columnName: string, data: Record<string, unknown>[]): string {
  for (const row of data) {
    const val = row[columnName];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const obj = val as Record<string, unknown>;
      if (isNodeLike(obj)) {
        return (obj.labels as string[])[0] || columnName;
      }
      if (isEdgeLike(obj)) {
        return obj.type as string;
      }
    }
    if (val !== null && val !== undefined) {
      return columnName.charAt(0).toUpperCase() + columnName.slice(1);
    }
  }
  return columnName.charAt(0).toUpperCase() + columnName.slice(1);
}

/**
 * Format a cell value for display.
 * Returns a string representation (React rendering handled by caller).
 */
export function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value !== 'object') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value.map((v) => formatCellValue(v)).join(', ');
  }

  const obj = value as Record<string, unknown>;

  // Graph node
  if (isNodeLike(obj)) {
    const props = extractProperties(obj, NODE_META_KEYS);
    const label = (obj.labels as string[])[0] || 'Node';
    const name = (props.name as string) || (props.Name as string) || (props.title as string) || '';
    const lines: string[] = [];
    lines.push(name ? `[${label}] ${name}` : `[${label}]`);
    const skipKeys = new Set(['name', 'Name', 'title']);
    for (const [k, v] of Object.entries(props)) {
      if (skipKeys.has(k)) continue;
      if (v === null || v === undefined) continue;
      const valStr = typeof v === 'object' ? JSON.stringify(v) : String(v);
      lines.push(`  ${k}: ${valStr.length > 80 ? valStr.slice(0, 80) + '...' : valStr}`);
    }
    return lines.join('\n');
  }

  // Graph relationship
  if (isEdgeLike(obj)) {
    const props = extractProperties(obj, EDGE_META_KEYS);
    const lines = [`[${obj.type}]`];
    for (const [k, v] of Object.entries(props)) {
      if (v === null || v === undefined) continue;
      lines.push(`  ${k}: ${String(v)}`);
    }
    return lines.join('\n');
  }

  // Generic object — show key-value pairs (max 2 levels)
  const lines: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'object' && !Array.isArray(v)) {
      lines.push(`${k}:`);
      for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) {
        if (v2 === null || v2 === undefined) continue;
        lines.push(`  ${k2}: ${String(v2)}`);
      }
    } else {
      const valStr = typeof v === 'object' ? JSON.stringify(v) : String(v);
      lines.push(`${k}: ${valStr.length > 100 ? valStr.slice(0, 100) + '...' : valStr}`);
    }
  }
  return lines.join('\n') || '—';
}

/**
 * Extract graph nodes and edges from Cypher result rows.
 * Handles:
 * - API format: {element_id, labels, properties: {}} and {element_id, type, source_element_id, target_element_id, properties: {}}
 * - record.data() format: {element_id, labels, ...props} and {element_id, type, start_node_element_id, end_node_element_id, ...props}
 * - Arrays (from variable-length paths like r*1..2)
 */
export function extractStepGraphData(data: Record<string, unknown>[]): { nodes: GraphNode[]; edges: GraphEdge[] } {
  const nodeMap = new Map<string, GraphNode>();
  const edgeMap = new Map<string, GraphEdge>();

  function processValue(val: unknown): void {
    if (!val || typeof val !== 'object') return;

    if (Array.isArray(val)) {
      for (const item of val) {
        processValue(item);
      }
      return;
    }

    const obj = val as Record<string, unknown>;

    if (isNodeLike(obj)) {
      const props = extractProperties(obj, NODE_META_KEYS);
      nodeMap.set(obj.element_id as string, {
        element_id: obj.element_id as string,
        labels: obj.labels as string[],
        properties: props,
      });
    }

    if (isEdgeLike(obj)) {
      const props = extractProperties(obj, EDGE_META_KEYS);
      const sourceId = (obj.source_element_id || obj.start_node_element_id) as string;
      const targetId = (obj.target_element_id || obj.end_node_element_id) as string;
      edgeMap.set(obj.element_id as string, {
        element_id: obj.element_id as string,
        type: obj.type as string,
        source_element_id: sourceId,
        target_element_id: targetId,
        properties: props,
      });
    }
  }

  for (const row of data) {
    for (const val of Object.values(row)) {
      processValue(val);
    }
  }

  return { nodes: Array.from(nodeMap.values()), edges: Array.from(edgeMap.values()) };
}
